import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useAnalyticsService from "../../../services/AnalyticsService";
import {
  DailyLiveStreamStats,
  MinutesViewedOverTimeResult,
  StreamedMinutesOverTimeResult,
  StreamsOverTimeResult,
} from "../../../types/Analytics";
import {
  LineChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  ResponsiveContainer,
} from "recharts";
import Ghost from "../../../components/Ghost";
import { utcMidnightToday, ymd } from "../../../utilities/DateUtilities";
import { useUrlDateRange } from "../../../hooks/useUrlDateRange";
import DateRangeToolbar from "../../../components/DateRangeToolbar";
import {
  convertToHoursAndMinutes,
  friendlyFormatNumber,
} from "../../../utilities/NumberUtilities";

export default function AdminAnalyticsStreamsSummaryPage() {
  // See if start and end dates were passed in the parameters.
  const { getDailyLiveStreamStats } = useAnalyticsService();

  const [isLoading, setIsLoading] = useState(false);

  const { startDate, endDate, setRange } = useUrlDateRange(7);

  const [dailyLiveStreamStats, setDailyLiveStreamStats] =
    useState<DailyLiveStreamStats | null>(null);
  const [countsForPeriod, setCountsForPeriod] = useState<any | null>(null);

  useEffect(() => {
    if (!startDate || !endDate) return;

    // Get the data.
    getData();
  }, [startDate, endDate]);

  const getData = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    getDailyLiveStreamStats(startDate!, endDate!)
      .then((result) => {
        
        console.debug("Daily Live Stream Stats:", result);

        setDailyLiveStreamStats(result);

        var totalStreams = result.dailyStreams.reduce(
          (sum, item) => sum + item.streamCount,
          0
        );
        var totalMinutesStreamed = result.dailyStreams.reduce(
          (sum, item) => sum + item.totalStreamMinutes,
          0
        );
        var totalMinutesViewed = result.dailyStreams.reduce(
          (sum, item) => sum + item.totalWatchMinutes,
          0
        );

        setCountsForPeriod({
          totalStreams: totalStreams,
          totalMinutesStreamed: totalMinutesStreamed,
          totalMinutesViewed: totalMinutesViewed,
        });
      }) 
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="grid grid-cols gap-4">
      <h2 className="text-2xl">Streams</h2>
      <section className="mt-4">
        {startDate && endDate && (
          <DateRangeToolbar
            startDate={startDate}
            endDate={endDate}
            onChange={setRange}
          />
        )}
      </section>
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <h3 className="text-lg">Total Streams (in period)</h3>
          <p className="text-3xl font-bold">
            {countsForPeriod && friendlyFormatNumber(countsForPeriod.totalStreams)}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="text-lg">Total Minutes Streamed (in period)</h3>
          <p className="text-3xl font-bold">
            {countsForPeriod && convertToHoursAndMinutes(countsForPeriod.totalMinutesStreamed)}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="text-lg">Total Minutes Viewed (in period)</h3>
          <p className="text-3xl font-bold">
            {countsForPeriod && convertToHoursAndMinutes(countsForPeriod.totalMinutesViewed)}
          </p>
        </div>
      </section>
      <section>
        <div className="grid grid-cols-1">
          <div className="grid grid-cols-1 xl:grid-cols-2">
            <div className="w-full">
              <h3 className="text-lg">Total Streams</h3>
              <div className="w-full h-64 md:h-80 min-w-0">
                {isLoading && <Ghost additionalClasses="w-full h-full" />}
                {!isLoading && dailyLiveStreamStats && (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyLiveStreamStats.dailyStreams}>
                      <Bar
                        name="Total streams"
                        dataKey="streamCount"
                        fill="#60a5fa"
                      />
                      <Legend />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: "#fff" }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />
                      <YAxis
                        tick={{ fill: "#fff" }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />
                      <Tooltip
                        labelStyle={{ color: "#000" }} // date/header
                        itemStyle={{ color: "#000" }} // series/value rows
                        contentStyle={{
                          backgroundColor: "#fff",
                          borderColor: "#e5e7eb",
                        }} // optional
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            <div className="w-full">
              <h3 className="text-lg">Duration</h3>
              <div className="w-full h-64 md:h-80 min-w-0">
                {isLoading && <Ghost additionalClasses="w-full h-full" />}
                {!isLoading && dailyLiveStreamStats && (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={dailyLiveStreamStats.dailyStreams}
                      margin={{ top: 28, right: 16, bottom: 0, left: 0 }}
                    >
                      <Line
                        type="monotone"
                        dataKey="totalStreamMinutes"
                        name="Minutes streamed"
                        stroke="#60a5fa" // blue-400
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="totalWatchMinutes"
                        name="Minutes watched"
                        stroke="#f59e0b" // amber-500
                        strokeWidth={2}
                        dot={false}
                      />
                      <Legend />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: "#fff" }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />
                      <YAxis
                        tick={{ fill: "#fff" }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />
                      <Tooltip
                        labelStyle={{ color: "#000" }}
                        itemStyle={{ color: "#000" }}
                        contentStyle={{
                          backgroundColor: "#fff",
                          borderColor: "#e5e7eb",
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
          <div className="p-4">
            {dailyLiveStreamStats && (
              <table className="table-auto w-full card border-spacing-2">
                <thead>
                  <tr>
                    <th className="font-normal text-left p-4">Date</th>
                    <th className="font-normal text-left p-4">Total Streams</th>
                    <th className="font-normal text-left p-4">
                      Total minutes streamed
                    </th>
                    <th className="font-normal text-left p-4">
                      Total minutes viewed
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dailyLiveStreamStats.dailyStreams.map((item) => (
                    <tr
                      key={item.date.toString()}
                      className="border-t border-cyan-900 hover:bg-cyan-300/25"
                    >
                      <td className="p-4">{item.date.toString().replace("T00:00:00", "")}</td>
                      <td className="p-4">{item.streamCount}</td>
                      <td className="p-4">
                        {item.totalStreamMinutes.toFixed()}
                      </td>
                      <td className="p-4">
                        {item.totalWatchMinutes.toFixed()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
      <section>
        <div className="grid grid-cols-1">
          <h2 className="text-lg font-semibold">Game minutes over period</h2>
          <div className="w-full h-64 md:h-80 min-w-0">
            {isLoading && <Ghost additionalClasses="w-full h-full" />}
            {!isLoading && dailyLiveStreamStats && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyLiveStreamStats.sumTitleStreams}>
                  <Bar
                    name="Total minutes streamed"
                    dataKey="totalStreamMinutes"
                    fill="#60a5fa"
                  />
                  <Line
                    name="Total minutes viewed"
                    dataKey="totalWatchMinutes"
                    stroke="#f43f5e"
                  />
                  <Legend />
                  <XAxis
                    dataKey="titleName"
                    tick={{ fill: "#fff" }}
                    axisLine={{ stroke: "#fff" }}
                    tickLine={{ stroke: "#fff" }}
                  />
                  <YAxis
                    tick={{ fill: "#fff" }}
                    axisLine={{ stroke: "#fff" }}
                    tickLine={{ stroke: "#fff" }}
                  />
                  <Tooltip
                    labelStyle={{ color: "#000" }} // date/header
                    itemStyle={{ color: "#000" }} // series/value rows
                    contentStyle={{
                      backgroundColor: "#fff",
                      borderColor: "#e5e7eb",
                    }} // optional
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          {dailyLiveStreamStats && (
            <table className="table-auto w-full card border-spacing-2">
              <thead>
                <tr>
                  <th className="font-normal text-left p-4">Game</th>
                  <th className="font-normal text-left p-4">
                    Total Minutes Streamed
                  </th>
                  <th className="font-normal text-left p-4">
                    Total Minutes Viewed
                  </th>
                </tr>
              </thead>
              <tbody>
                {dailyLiveStreamStats.sumTitleStreams.map((item) => (
                  <tr
                    key={item.titleId.toString()}
                    className="border-t border-cyan-900 hover:bg-cyan-300/25"
                  >
                    <td className="p-4">{item.titleName}</td>
                    <td className="p-4">
                      {item.totalStreamMinutes.toFixed()}
                    </td>
                    <td className="p-4">{item.totalWatchMinutes.toFixed()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
